import { Error, NotInterested, Store } from '@mui/icons-material'
import { I18n } from 'common'
import { InventoryDisplay, ProductLocationModel } from 'interfaces'
import { ReactElement } from 'react'
import WarningLabel from 'src/features/WarningLabel'
import { LocationType } from 'src/helpers/locationType'
import { defaultTheme } from 'src/theme'
import styled from 'styled-components'
import { getLocationLinkLabelKey } from './getLocationLinkLabelKey'

interface ProductCardMobileAvailabilityProps {
  isCondensedView: boolean
  invDisplayState: InventoryDisplay
  activeLocation?: ProductLocationModel
  qtyAvailable?: number
  locType?: string
  called?: string
  inStockSomewhere: boolean
  isZeroCost: boolean
  contactNumber: string
  isMultiLocationEnabled: boolean
  showLocationSelector: () => void
}

export const ProductCardMobileAvailability = ({
  isCondensedView,
  invDisplayState,
  activeLocation,
  qtyAvailable,
  locType,
  called,
  inStockSomewhere,
  isZeroCost,
  contactNumber,
  isMultiLocationEnabled,
  showLocationSelector,
}: ProductCardMobileAvailabilityProps): ReactElement => {
  const iconSize = isCondensedView ? '20px' : '24px'

  return (
    <MobileLocationAvailability>
      {invDisplayState === InventoryDisplay.AVAILABLE_AT_LOCATION && (
        <Wrap className="fw-bold">
          {qtyAvailable}&nbsp;
          <I18n name="at" />
          &nbsp;
          {locType === 'primary' && (
            <Store
              htmlColor={defaultTheme.colors.green}
              sx={{ width: iconSize, height: iconSize }}
            />
          )}
          {called}
        </Wrap>
      )}
      {invDisplayState === InventoryDisplay.SHOW_ZERO && (
        <Wrap className="fw-bold">
          <ZeroStockAvailability
            callForAvailability={!inStockSomewhere && isZeroCost}
            called={called}
            iconSize={iconSize}
            isCondensedView={isCondensedView}
          />
        </Wrap>
      )}
      {invDisplayState === InventoryDisplay.CALL_FOR_AVAILABILITY && (
        <>
          <Wrap className="fw-bold">
            <Error
              htmlColor={defaultTheme.colors.primary}
              sx={{ width: iconSize, height: iconSize }}
            />
            &nbsp;
            <I18n
              name="callForAvailability"
              style={{ fontSize: isCondensedView && '14px' }}
            />
          </Wrap>
          <Description>
            <I18n
              name="itemUnavailableAtAllLocations"
              style={{ fontSize: isCondensedView && '14px' }}
            />
            <br />
            {contactNumber && (
              <I18n
                name="pleaseContactForInformation"
                textReplacement={[contactNumber]}
                style={{ fontSize: isCondensedView && '14px' }}
              />
            )}
          </Description>
        </>
      )}
      {activeLocation?.locType === LocationType.ORDER_FROM_PRIMARY && (
        <WarningLabel
          name="orderFromPrimaryWarining"
          style={{ width: '150px' }}
        />
      )}
      {inStockSomewhere && (
        <Wrap>
          <LocalLink
            onClick={showLocationSelector}
            style={{ marginTop: isCondensedView && '4px' }}
          >
            <I18n name={getLocationLinkLabelKey(isMultiLocationEnabled)} />
          </LocalLink>
        </Wrap>
      )}
    </MobileLocationAvailability>
  )
}

const ZeroStockAvailability = ({
  callForAvailability,
  called,
  iconSize,
  isCondensedView,
}: {
  callForAvailability: boolean
  called?: string
  iconSize: string
  isCondensedView: boolean
}): ReactElement => {
  if (callForAvailability) {
    return (
      <>
        <NotInterested
          htmlColor={defaultTheme.colors.red}
          sx={{ width: iconSize, height: iconSize }}
        />
        <I18n
          name="callForAvailability"
          style={{ fontSize: isCondensedView && '14px' }}
        />
      </>
    )
  }

  return <I18n name="zeroAvailableAtLocation" textReplacement={[called]} />
}

const MobileLocationAvailability = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Wrap = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

const LocalLink = styled.div`
  color: ${defaultTheme.colors.link};
  text-decoration: underline;
  cursor: pointer;
`

const Description = styled.div`
  font-size: 14px;
  line-height: 20px;
`
